import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { formatSalesValue } from "../../../utils/formatUtils";
import { IoClose } from "react-icons/io5";

const ItemTable = ({
    type = "food",
    items = [],
    FoodBeverageSum,
    outlet,
    token,
    startDate,
    endDate,
    url,
    excludeComplementary,
    onlyComplementary,
    summaryQuantity,
    summaryTotal,
    discountData,
}) => {
    const tableRef = useRef(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemDetails, setItemDetails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // ── Derived config from `type` ──────────────────────────────────────────────
    const isFood = type === "food";
    const title = isFood ? "Food" : "Beverage";
    const exportFilename = isFood ? "foods_table.xlsx" : "beverages_table.xlsx";
    const exportSheetName = isFood ? "Foods" : "Beverages";

    // ── Discount ────────────────────────────────────────────────────────────────
    const discountSection = discountData?.[type] ?? null;
    const totalAmount = discountSection?.total_amount ?? null;
    const totalDiscount = discountSection?.total_discount ?? null;
    const hasDiscount =
        discountSection !== null && totalDiscount !== null && totalDiscount > 0;

    // When complementary is included, the base for discount % and net shifts
    // to the live summaryTotal instead of the API-returned total_amount
    const effectiveBase = !excludeComplementary && !onlyComplementary
        ? summaryTotal
        : totalAmount;

    const discountPct =
        hasDiscount && effectiveBase
            ? (totalDiscount / effectiveBase) * 100
            : discountSection?.discount_percentage ?? null;

    const netAmount =
        hasDiscount && effectiveBase != null
            ? effectiveBase - totalDiscount
            : discountSection?.net_amount ?? null;

    // ── Quantity / total helpers ─────────────────────────────────────────────────
    const resolveQuantity = (item) =>
        onlyComplementary
            ? Number(item.complementaryQuantity || 0)
            : excludeComplementary
                ? Number(item.quantity || 0)
                : Number(item.quantity || 0) + Number(item.complementaryQuantity || 0);

    const resolveTotal = (item) =>
        onlyComplementary
            ? Number(item.complementaryTotal || 0)
            : excludeComplementary
                ? Number(item.total || 0)
                : Number(item.total || 0) + Number(item.complementaryTotal || 0);

    // ── Modal ────────────────────────────────────────────────────────────────────
    const fetchItemDetails = async (itemName) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${url}/itemssoldcountbillwise/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    outlet,
                    dateStart: startDate,
                    dateEnd: endDate,
                    itemName,
                    token,
                }),
            });
            if (!response.ok) throw new Error("Failed to fetch data");
            setItemDetails(await response.json());
            setIsModalOpen(true);
        } catch {
            setError("Error fetching item details");
        } finally {
            setLoading(false);
        }
    };

    const openModal = (item) => {
        setSelectedItem(item);
        fetchItemDetails(item.itemName);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedItem(null);
        setItemDetails([]);
    };

    const filteredDetails = onlyComplementary
        ? itemDetails.filter((row) => !row.Bill_No || row.Bill_No.trim() === "")
        : excludeComplementary
            ? itemDetails.filter((row) => row.Bill_No && row.Bill_No.trim() !== "")
            : itemDetails;

    const visibleData = (items || [])
        .filter((item) => onlyComplementary ? item.hasComplementary : true)
        .filter((item) => excludeComplementary ? !item.isComplementaryOnly : true);

    // ── Export ───────────────────────────────────────────────────────────────────
    const handleExport = () => {
        if (!visibleData || visibleData.length === 0) return;

        const data = [["Group", "Item Name", "Item Rate (Rs)", "Quantity", "Total (Rs)"]];
        let totalQty = 0;

        visibleData.forEach((item) => {
            const quantity = resolveQuantity(item);
            const total = resolveTotal(item);
            totalQty += quantity;
            data.push([item.Description, item.itemName, item.itemrate, quantity, total.toFixed(2)]);
        });

        data.push([]);
        data.push(["", "", "", "Total Quantity", totalQty]);

        if (hasDiscount) {
            data.push(["", "", "", "Gross Total (Rs)", formatSalesValue(effectiveBase)]);
            data.push(["", "", "", `Discount (${discountPct?.toFixed(2)}%)`, `-${formatSalesValue(totalDiscount)}`]);
            data.push(["", "", "", "Net Total (Rs)", formatSalesValue(netAmount)]);
        } else {
            data.push(["", "", "", "Grand Total (Rs)", formatSalesValue(summaryTotal)]);
        }

        const ws = XLSX.utils.aoa_to_sheet(data);
        ws["!cols"] = [{ wch: 30 }, { wch: 40 }, { wch: 15 }, { wch: 15 }, { wch: 18 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, exportSheetName);
        XLSX.writeFile(wb, exportFilename);
    };

    // ── Render ───────────────────────────────────────────────────────────────────
    return (
        <>
            <div className="w-full">
                <div className="mb-2 flex items-center justify-between">
                    <div className="text-base font-semibold text-gray-900">{title}</div>
                    <button
                        onClick={handleExport}
                        className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
                    >
                        Export
                    </button>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
                    <div className="max-h-[620px] overflow-auto tiny-scrollbar" ref={tableRef}>
                        <table className="min-w-full border-collapse text-sm text-center">
                            <thead className="sticky top-0 z-10 bg-gray-100">
                                <tr className="border-b border-gray-200">
                                    {["Group", "Item Name", "Rate", "Quantity", "Total"].map((h) => (
                                        <th
                                            key={h}
                                            className="p-2 text-xs font-semibold uppercase tracking-wider text-gray-600"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {visibleData.map((item, index) => {
                                    const quantity = resolveQuantity(item);
                                    const total = resolveTotal(item);
                                    return (
                                        <tr
                                            key={index}
                                            className="border-b border-gray-100 transition-colors hover:bg-gray-50 cursor-pointer"
                                            onClick={() => openModal(item)}
                                        >
                                            <td className="px-2 py-1.5 font-medium text-gray-700">{item.Description}</td>
                                            <td className="px-2 py-1.5 font-medium text-gray-900">{item.itemName}</td>
                                            <td className="px-2 py-1.5 font-medium text-gray-700">{formatSalesValue(item.itemrate)}</td>
                                            <td className="px-2 py-1.5 font-medium text-gray-900">{quantity}</td>
                                            <td className="px-2 py-1.5 font-semibold text-gray-900">{formatSalesValue(total)}</td>
                                        </tr>
                                    );
                                })}

                                {visibleData.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="py-6 text-center text-sm text-gray-500">
                                            No data available
                                        </td>
                                    </tr>
                                )}
                            </tbody>

                            {FoodBeverageSum && (
                                <tfoot className="sticky bottom-0 bg-gray-100">
                                    <tr className="border-t border-gray-200">
                                        <td colSpan={3}></td>
                                        <td className="p-2 font-semibold text-gray-900">{summaryQuantity}</td>
                                        <td className="p-2 font-semibold text-gray-900">
                                            {hasDiscount && !onlyComplementary
                                                ? formatSalesValue(effectiveBase)
                                                : formatSalesValue(summaryTotal)}
                                        </td>
                                    </tr>

                                    {hasDiscount && !onlyComplementary && (
                                        <tr className="border-t border-gray-300">
                                            <td colSpan={3} className="p-2 font-medium text-right text-red-500">
                                                Discount ({discountPct?.toFixed(2)}%)
                                            </td>
                                            <td></td>
                                            <td className="p-2 font-semibold text-red-500">
                                                -{formatSalesValue(totalDiscount)}
                                            </td>
                                        </tr>
                                    )}

                                    {hasDiscount && !onlyComplementary && (
                                        <tr className="border-t border-gray-300">
                                            <td colSpan={3} className="p-2 font-medium text-right text-gray-700">
                                                Net Total
                                            </td>
                                            <td></td>
                                            <td className="p-2 font-semibold text-gray-900">
                                                {formatSalesValue(netAmount)}
                                            </td>
                                        </tr>
                                    )}
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            </div>

            {/* ── Modal ── */}
            {isModalOpen && selectedItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
                    <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">

                        <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-center">
                            <div className="flex items-center gap-5 text-base text-gray-700">
                                <div className="text-lg font-semibold text-gray-900">{selectedItem.itemName}</div>
                                <span><strong>Group:</strong> {selectedItem.Description}</span>
                                <span className="font-semibold">
                                    Sales Report from {new Date(startDate).toLocaleDateString()} to{" "}
                                    {new Date(endDate).toLocaleDateString()}
                                </span>
                            </div>
                            <button
                                onClick={closeModal}
                                className="p-1.5 rounded-lg text-gray-600 transition hover:bg-gray-100 hover:text-gray-700"
                            >
                                <IoClose className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="flex-1 min-h-0 px-4 py-3 flex">
                            {loading ? (
                                <div className="flex h-full items-center justify-center text-sm text-gray-600 p-3">
                                    Loading...
                                </div>
                            ) : error ? (
                                <div className="p-3 text-red-500">{error}</div>
                            ) : (
                                <div className="flex-1 overflow-auto rounded-lg border border-gray-200 tiny-scrollbar">
                                    <table className="min-w-full border-collapse text-sm text-center">
                                        <thead className="sticky top-0 z-10 bg-gray-100">
                                            <tr className="border-b border-gray-200">
                                                {["Date", "Bill No", "Type", "Start Time", "End Time", "Employee", "Guests", "Guest Name", "Qty", "Rate", "Total"].map((h) => (
                                                    <th key={h} className="p-3 text-xs font-semibold uppercase tracking-wide text-gray-600">
                                                        {h}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {filteredDetails.length > 0 ? (
                                                filteredDetails.map((row, index) => (
                                                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                                                        <td className="px-3 py-2.5 text-gray-700">{row.Date}</td>
                                                        <td className="px-3 py-2.5 font-medium text-gray-900">{row.Bill_No || "-"}</td>
                                                        <td className="px-3 py-2.5 text-gray-700">{row.Type}</td>
                                                        <td className="px-3 py-2.5 text-gray-700">{row.Start_Time}</td>
                                                        <td className="px-3 py-2.5 text-gray-700">{row.End_Time}</td>
                                                        <td className="px-3 py-2.5 text-gray-700">{row.Employee}</td>
                                                        <td className="px-3 py-2.5 text-gray-700">{row.NoOfGuests}</td>
                                                        <td className="px-3 py-2.5 text-gray-700">{row.GuestName || "N/A"}</td>
                                                        <td className="px-3 py-2.5 font-medium text-gray-900">{Number(row.Count || 0)}</td>
                                                        <td className="px-3 py-2.5 text-gray-700">{formatSalesValue(row.ItemRate)}</td>
                                                        <td className="px-3 py-2.5 font-semibold text-gray-900">{formatSalesValue(row.Total)}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={11} className="py-6 text-center text-sm text-gray-600">
                                                        No data available
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>

                                        {filteredDetails.length > 0 && (
                                            <tfoot className="sticky bottom-0 bg-gray-100">
                                                <tr>
                                                    <td className="px-3 py-2.5 font-semibold text-gray-900">Total</td>
                                                    <td className="px-3 py-2.5 font-semibold text-gray-900">{filteredDetails.length}</td>
                                                    <td colSpan={6}></td>
                                                    <td className="px-3 py-2.5 font-semibold text-gray-900">
                                                        {filteredDetails.reduce((sum, row) => sum + Number(row.Count || 0), 0)}
                                                    </td>
                                                    <td></td>
                                                    <td className="px-3 py-2.5 font-semibold text-gray-900">
                                                        {formatSalesValue(filteredDetails.reduce((sum, row) => sum + Number(row.Total || 0), 0))}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ItemTable;